/**
 * P4-10: Options 对话框内容。
 *
 * 取代 index.html `#optionsDiv` 与 `js/ui.js:updateOptions` 中布尔选项渲染部分。
 * 非布尔控件（语言、记数法、主题、batch size）与布尔选项（来自 `game.settings.settingsArr`）
 * 统一在一个 React dialog 内管理。
 *
 * 使用方式：通过 DialogProvider 的 openDialog 打开，例如
 * ```tsx
 * openDialog({ id: "options", title: $I("ui.options"), children: <OptionsDialog /> });
 * ```
 *
 * eslint-disable react-hooks/immutability
 * 本组件是旧引擎可变状态（game.opts）与 React 事件处理之间的桥接层；
 * 事件回调中直接修改 game.opts 是为保持与旧 SettingsTab 一致的行为，
 * 且这些修改发生在用户交互期间，不会导致 render-phase 不一致。
 */
/* eslint-disable react-hooks/immutability */
import { useCallback, useMemo, useState } from "react";
import { useGameTick } from "../bridge/useGameTick";
import { getGame, type GamePage } from "../bridge/GameBridge";
import { t } from "../bridge/i18n";
import type { DialogApi } from "./DialogHost";

function getDialogApi(): DialogApi | undefined {
  return (window as { KGNewUI?: { dialogs?: DialogApi } }).KGNewUI?.dialogs;
}

interface SettingDef {
  name: string;
  defaultValue: boolean;
  label?: string;
  tooltip?: string;
  isExtra?: boolean;
  triggerUpdateUI?: boolean;
  devModeOnly?: boolean;
  showOnlyIfKSDetected?: boolean;
}

interface OptionsSnapshot {
  opts: Record<string, unknown>;
  colorScheme: string;
  unlockedSchemes: string[];
  devMode: boolean;
  settingsArr: SettingDef[];
}

function getOptionsSnapshot(game: GamePage | null): OptionsSnapshot | null {
  if (!game) {
    return null;
  }
  return {
    opts: { ...(game.opts ?? {}) },
    colorScheme: game.colorScheme ?? "default",
    unlockedSchemes: [...(game.unlockedSchemes ?? [])],
    devMode: !!game.devMode,
    settingsArr: [...(game.settings?.settingsArr ?? [])],
  };
}

function snapEq(a: OptionsSnapshot | null, b: OptionsSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.colorScheme !== b.colorScheme ||
    a.devMode !== b.devMode ||
    a.unlockedSchemes.length !== b.unlockedSchemes.length ||
    a.settingsArr.length !== b.settingsArr.length
  ) {
    return false;
  }
  for (let i = 0; i < a.unlockedSchemes.length; i++) {
    if (a.unlockedSchemes[i] !== b.unlockedSchemes[i]) {
      return false;
    }
  }
  for (let i = 0; i < a.settingsArr.length; i++) {
    const sa = a.settingsArr[i];
    const sb = b.settingsArr[i];
    if (
      sa.name !== sb.name ||
      sa.label !== sb.label ||
      sa.isExtra !== sb.isExtra ||
      sa.devModeOnly !== sb.devModeOnly ||
      sa.showOnlyIfKSDetected !== sb.showOnlyIfKSDetected
    ) {
      return false;
    }
  }
  const keysA = Object.keys(a.opts);
  const keysB = Object.keys(b.opts);
  if (keysA.length !== keysB.length) {
    return false;
  }
  for (const key of keysA) {
    if (a.opts[key] !== b.opts[key]) {
      return false;
    }
  }
  return true;
}

function getI18nLang() {
  return (globalThis as { i18nLang?: { getLanguage: () => string; getAvailableLocales: () => string[]; getAvailableLocaleLabels: () => Record<string, string>; updateLanguage: (lang: string) => void } }).i18nLang;
}

function getClasses() {
  return (globalThis as { classes?: { KGConfig: new () => { statics: { notations: string[] } } } }).classes;
}

function getUi() {
  const game = getGame();
  return game?.ui;
}

export default function OptionsDialog() {
  const selector = useCallback((game: GamePage | null) => getOptionsSnapshot(game), []);
  const snapshot = useGameTick(selector, snapEq);
  const game = getGame();
  const ui = getUi();

  const i18nLang = getI18nLang();
  const currentLang = i18nLang?.getLanguage() ?? "en";
  const locales = useMemo(() => i18nLang?.getAvailableLocales() ?? ["en"], [i18nLang]);
  const localeLabels = useMemo(() => i18nLang?.getAvailableLocaleLabels() ?? { en: "English" }, [i18nLang]);

  const notations = useMemo(() => getClasses()?.KGConfig ? new (getClasses() as NonNullable<ReturnType<typeof getClasses>>).KGConfig().statics.notations : [], []);

  const allSchemes = ui?.allSchemes ?? ["default", "dark", "sleek"];
  const defaultSchemes = ui?.defaultSchemes ?? ["default"];

  const [selectedLang, setSelectedLang] = useState(currentLang);
  const [showExtra, setShowExtra] = useState(false);

  if (!snapshot || !game) {
    return null;
  }

  const handleLangChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLang(e.target.value);
  };

  const handleApplyLanguage = () => {
    i18nLang?.updateLanguage(selectedLang);
    game.updateOptionsUI?.();
    // 与旧实现一致：换语言后整页刷新
    window.location.reload();
  };

  const handleNotationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // eslint-disable-next-line react-hooks/immutability
    game.opts.notation = e.target.value;
    game.updateOptionsUI?.();
  };

  const handleSchemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    (globalThis as { gamePage?: { toggleScheme: (scheme: string) => void } }).gamePage?.toggleScheme?.(e.target.value);
  };

  const handleBatchSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    // eslint-disable-next-line react-hooks/immutability
    game.opts.batchSize = Math.max(1, Math.min(9999, val));
  };

  const handleToggleSetting = (setting: SettingDef) => {
    const next = !game.opts[setting.name];
    // eslint-disable-next-line react-hooks/immutability
    game.opts[setting.name] = next;
    if (setting.triggerUpdateUI) {
      game.ui?.updateOptions?.();
    }
  };

  const handleRelock = () => {
    ui?.relockSchemes?.();
  };

  const renderSetting = (setting: SettingDef) => {
    if (!snapshot.devMode) {
      if (setting.devModeOnly || setting.showOnlyIfKSDetected || !setting.label) {
        return null;
      }
    }
    const id = `${setting.name}I`;
    return (
      <div key={setting.name} className="option-row">
        <input
          id={id}
          type="checkbox"
          checked={!!game.opts[setting.name]}
          onChange={() => handleToggleSetting(setting)}
        />
        <label
          htmlFor={id}
          title={setting.devModeOnly ? t("ui.option.dev.mode.only") : undefined}
          dangerouslySetInnerHTML={{ __html: setting.label ?? "" }}
        />
        {setting.tooltip && (
          <span className="option-hint" style={{ cursor: "help" }} dangerouslySetInnerHTML={{ __html: " (?)" }} />
        )}
      </div>
    );
  };

  const normalSettings = snapshot.settingsArr.filter((s) => !s.isExtra);
  const extraSettings = snapshot.settingsArr.filter((s) => s.isExtra);

  return (
    <div className="options-dialog">
      <div className="options-top-controls" style={{ display: "flex", gap: "25px", flexWrap: "wrap", alignItems: "flex-start" }}>
        <div>
          <label htmlFor="languageSelector">{t("ui.option.language")}</label>
          <br />
          <select
            id="languageSelector"
            value={selectedLang}
            onChange={handleLangChange}
            style={{ marginTop: "2px" }}
          >
            {locales.map((locale) => (
              <option key={locale} value={locale}>{localeLabels[locale] ?? locale}</option>
            ))}
          </select>
          <br />
          {selectedLang !== currentLang && (
            <a
              id="languageApplyLink"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleApplyLanguage();
              }}
              style={{ fontSize: "14px" }}
            >
              {t("ui.option.language.apply")}
            </a>
          )}
        </div>

        <div>
          <label htmlFor="notationSelector">{t("ui.option.notation")}</label>
          <br />
          <select
            id="notationSelector"
            value={(game.opts.notation as string) ?? "default"}
            onChange={handleNotationChange}
            style={{ marginTop: "2px" }}
          >
            {notations.map((n) => (
              <option key={n} value={n}>{t(`opts.notation.${n}`)}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="schemeToggle">{t("ui.option.scheme")}</label>
          {" "}
          <span id="schemeTip" style={{ cursor: "help", paddingLeft: "4px" }}>(?)</span>
          <br />
          <select
            id="schemeToggle"
            value={snapshot.colorScheme}
            onChange={handleSchemeChange}
            style={{ marginTop: "2px" }}
          >
            {allSchemes.map((scheme: string) => {
              const unlocked = snapshot.unlockedSchemes.includes(scheme) || defaultSchemes.includes(scheme);
              return (
                <option key={scheme} value={scheme} disabled={!unlocked}>
                  {unlocked ? t(`opts.theme.${scheme}`) : `  ${t(`opts.theme.${scheme}`)}`}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <br />
      <div id="booleanOptionsNormal">{normalSettings.map(renderSetting)}</div>
      <br />

      <a
        id="optionMore"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          setShowExtra((s) => !s);
        }}
      >
        {t("ui.option.more")}
      </a>

      {showExtra && (
        <div className="optsExtra">
          <div id="booleanOptionsExtra">{extraSettings.map(renderSetting)}</div>
          <input
            id="batchSize"
            type="number"
            min={1}
            max={9999}
            size={4}
            value={Number(game.opts.batchSize) || 1}
            onChange={handleBatchSizeChange}
          />
          <label htmlFor="batchSize">{t("ui.option.batch.size")}</label>
          <br />
          <a id="addTranslationLink" target="_blank" rel="noreferrer" href="https://crowdin.com/project/kittensgame">
            {t("ui.option.translation.portal")}
          </a>
          <br />
          <a
            id="schemeRelock"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleRelock();
            }}
          >
            {t("ui.option.relock.schemes")}
          </a>
        </div>
      )}

      <br />
      <input id="exportButton" type="button" value={t("ui.export")} onClick={() => getDialogApi()?.showExport()} />
      <input
        id="importButton"
        type="button"
        value={t("ui.import")}
        style={{ marginTop: "10px" }}
        onClick={() => getDialogApi()?.showImport()}
      />
    </div>
  );
}
